import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ConfirmDialogTitle } from "@/components/ui/ConfirmDialog/ConfirmDialogTitle";
import { ConfirmDialogBody } from "@/components/ui/ConfirmDialog/ConfirmDialogBody";
import { ConfirmDialogActions } from "@/components/ui/ConfirmDialog/ConfirmDialogActions";
import { useCategoryDocumentCount } from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";

interface CategoryDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  categoryName: string;
  categoryId: Id<"documentCategories"> | null;
  onConfirm: () => Promise<void>;
}

export function CategoryDeleteDialog({
  open,
  onClose,
  categoryName,
  categoryId,
  onConfirm,
}: CategoryDeleteDialogProps) {
  const [loading, setLoading] = useState(false);
  const { count: documentCount, isLoading: isCountLoading } =
    useCategoryDocumentCount(categoryId);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const canDelete = documentCount === 0;

  return (
    <ConfirmDialog open={open} onClose={onClose} forced>
      <ConfirmDialogTitle>Delete Category</ConfirmDialogTitle>
      <ConfirmDialogBody>
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm text-primary">
                Are you sure you want to delete the category{" "}
                <span className="font-medium">"{categoryName}"</span>?
              </p>
              {isCountLoading ? (
                <p className="text-sm text-secondary">
                  Checking document usage...
                </p>
              ) : documentCount > 0 ? (
                <p className="text-sm text-amber-600">
                  This category is currently used by {documentCount} document
                  {documentCount === 1 ? "" : "s"}. You must reassign or remove
                  these documents before deleting this category.
                </p>
              ) : (
                <p className="text-sm text-secondary">
                  This category is not currently used by any documents.
                </p>
              )}
            </div>
          </div>
        </div>
      </ConfirmDialogBody>
      <ConfirmDialogActions
        onConfirm={handleConfirm}
        onCancel={onClose}
        confirmLabel="Delete"
        variant="danger"
        confirmDisabled={!canDelete || isCountLoading}
        loading={loading}
      />
    </ConfirmDialog>
  );
}
